import { DownloadSimpleIcon } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { Button } from '~/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '~/components/ui/dropdown-menu';
import { downloadAppHtml, openAppPrint } from '~/features/apps/app-download';

export interface AppDownloadMenuProps {
  /** An app's own `/apps/<slug>/` — everything this control does is
   *  derived from this one value (see `app-download.ts`). */
  path: string;
  /** Used for the fallback filename ("<title>.html") and the trigger's
   *  accessible name ("Download <title>"). */
  title: string;
  /** The generic noun to fall back to in the saved filename when the
   *  title itself has nothing usable ASCII content — "deck", "sketch",
   *  etc. Defaults to "app". */
  fallbackWord?: string;
  /** Every surface that mounts this (a gallery card, a chat tool card)
   *  passes its own prefix so the trigger and its two items get stable,
   *  non-colliding `data-testid`s — the trigger is `<prefix>`, the items
   *  are `<prefix>-html` / `<prefix>-pdf`. */
  testIdPrefix: string;
  /** Positioning/visibility classes the caller's own card layout needs
   *  (e.g. a stretched-anchor card's `pointer-events-auto`). */
  className?: string;
}

/**
 * FRONT-2024: generalized out of `~/features/sketches/sketch-download-menu.tsx`
 * (FRONT-1809) so any single-document Eddy App can offer the same Download
 * control — first for Slides (decks), and reusable unchanged by Sketches
 * once FRONT-2025 moves them onto the Apps store. Two actions:
 *   - "HTML file" fetches the app's own raw HTML and saves it as a file.
 *   - "Save as PDF" opens the app's dedicated print page in a new tab,
 *     which triggers the browser's own print sheet — worded "Save as PDF"
 *     rather than "Print" because that's the exact button the user sees
 *     next inside that sheet, so the two read as one continuous action.
 *
 * The trigger is a plain `Button` used as `DropdownMenuTrigger`'s `asChild`
 * target with its own `tooltip` prop set — the safe shape per
 * `components/ui/tip.tsx`'s own docblock (`Button` forwards every prop
 * Radix's Slot clones onto it, all the way down to the real `<button>`,
 * before wrapping that finished element in `Tip`; a bare `Tip` used
 * directly as the trigger's child would instead drop them). `tooltip` is a
 * hint only, so the button still carries its own `aria-label`.
 */
export function AppDownloadMenu({ path, title, fallbackWord = 'app', testIdPrefix, className }: AppDownloadMenuProps) {
  async function handleDownloadHtml() {
    try {
      await downloadAppHtml(path, title, fallbackWord);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Download failed');
    }
  }

  // `openAppPrint` refuses a path that is not an app address (FRONT-1809
  // security review, carried over unchanged), so this is the one place
  // that refusal has to become something the user can read — an uncaught
  // throw inside a menu item's `onSelect` closes the menu and says
  // nothing at all.
  function handleSaveAsPdf() {
    try {
      openAppPrint(path);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not open the print view');
    }
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon-xs"
          tooltip="Download"
          aria-label={`Download ${title}`}
          data-testid={testIdPrefix}
          className={className}
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
          }}
        >
          <DownloadSimpleIcon size={13} />
        </Button>
      </DropdownMenuTrigger>
      {/* Same guard `work-card-menu.tsx` uses: React's synthetic events
          bubble through the component tree, not the physical DOM, so a
          portal-rendered item click would otherwise still reach whatever
          the trigger sits on top of (a stretched-anchor card overlay). */}
      <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
        <DropdownMenuItem data-testid={`${testIdPrefix}-html`} onSelect={() => void handleDownloadHtml()}>
          HTML file
        </DropdownMenuItem>
        <DropdownMenuItem data-testid={`${testIdPrefix}-pdf`} onSelect={handleSaveAsPdf}>
          Save as PDF
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
